// ===== High Tide v5.9 — optimized page-wide scroll & ambient animations =====
// Keeps the same visual motion while reducing RAF loops, ScrollTrigger count,
// and pointer-event work. Native scrolling is used so the page remains
// responsive while the WebGL can is active.
(() => {
  if (!document.querySelector('.ht-home-v58') || !window.gsap || !window.ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);
  ScrollTrigger.config({ limitCallbacks: true, ignoreMobileResize: true });
  window.__htLenis = null;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- tide-line heading reveal ---------- */
  gsap.fromTo('.tide-line h2',
    { y: '45%', scale: 0.92, autoAlpha: 0 },
    {
      y: '0%', scale: 1, autoAlpha: 1, ease: 'none',
      scrollTrigger: { trigger: '.tide-line', start: 'top 90%', end: 'top 45%', scrub: true },
    });

  /* ---------- floating decorations ---------- */
  const decos = gsap.utils.toArray('.deco');
  gsap.from(decos, {
    scale: 0,
    rotation: () => gsap.utils.random(-35, 35),
    ease: 'elastic.out(1, 0.5)',
    duration: 1,
    stagger: 0.07,
    scrollTrigger: { trigger: '.green-section', start: 'top 70%', once: true },
  });

  // One parallax trigger controls every decoration instead of one trigger per item.
  if (decos.length) {
    const parallax = gsap.timeline({
      scrollTrigger: {
        trigger: '.green-section',
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });
    decos.forEach((el, i) => {
      parallax.to(el, {
        y: (i % 2 ? -1 : 1) * gsap.utils.random(36, 86),
        ease: 'none',
      }, 0);
    });
  }

  // The gentle bobbing is still present, but one IntersectionObserver pauses
  // all loops when the green section is outside the viewport.
  if (!reducedMotion && decos.length) {
    const ambientTweens = decos.map((el) => gsap.to(el, {
      yPercent: gsap.utils.random(-14, 14),
      rotation: `+=${gsap.utils.random(-9, 9)}`,
      duration: gsap.utils.random(2.4, 4.4),
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
      delay: gsap.utils.random(0, 1.5),
      paused: true,
    }));

    const greenSection = document.querySelector('.green-section');
    if (greenSection && 'IntersectionObserver' in window) {
      const ambientObserver = new IntersectionObserver(([entry]) => {
        ambientTweens.forEach((tween) => entry.isIntersecting ? tween.resume() : tween.pause());
      }, { rootMargin: '12% 0px 12% 0px' });
      ambientObserver.observe(greenSection);
    } else {
      ambientTweens.forEach((tween) => tween.resume());
    }
  }

  /* ---------- values columns ---------- */
  const valueCols = gsap.utils.toArray('.value-col');
  if (valueCols.length) {
    gsap.fromTo(valueCols,
      {
        y: '38%',
        rotation: (i) => (i % 2 ? 4 : -4),
        autoAlpha: 0,
      },
      {
        y: '0%', rotation: 0, autoAlpha: 1, ease: 'none', stagger: 0.035,
        scrollTrigger: { trigger: '.values-grid', start: 'top 95%', end: 'top 45%', scrub: true },
      });
  }
  gsap.from('.value-icon', {
    scale: 0,
    ease: 'elastic.out(1, 0.45)',
    duration: 0.9,
    stagger: 0.12,
    scrollTrigger: { trigger: '.values-grid', start: 'top 60%', once: true },
  });

  /* ---------- badges ---------- */
  document.addEventListener('hightide:docked', () => {
    gsap.fromTo(['.vegan-badge', '.warning-badge'],
      { scale: 0.3 },
      { scale: 1, ease: 'elastic.out(1, 0.45)', duration: 0.8, stagger: 0.12, overwrite: true });
  });

  /* ---------- products ---------- */
  gsap.from('.products-head h2, .products-head p', {
    y: 50, autoAlpha: 0, stagger: 0.1, ease: 'power3.out', duration: 0.8,
    scrollTrigger: { trigger: '.products', start: 'top 75%', once: true },
  });
  gsap.from('.product-card', {
    y: 90,
    rotation: (i) => (i % 2 ? 3 : -3),
    autoAlpha: 0,
    stagger: 0.09,
    ease: 'back.out(1.6)',
    duration: 0.9,
    scrollTrigger: { trigger: '.product-grid', start: 'top 82%', once: true },
  });

  if (!reducedMotion) {
    const badge = document.querySelector('.gf-badge');
    if (badge) {
      const badgeTween = gsap.to(badge, { rotation: '-=11', duration: 2.4, yoyo: true, repeat: -1, ease: 'sine.inOut', paused: true });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          entry.isIntersecting ? badgeTween.resume() : badgeTween.pause();
        }, { rootMargin: '10% 0px' }).observe(badge);
      } else {
        badgeTween.resume();
      }
    }
  }

  if (finePointer) {
    document.querySelectorAll('.product-card').forEach((card) => {
      gsap.set(card, { transformPerspective: 800 });
      const rotationY = gsap.quickTo(card, 'rotationY', { duration: 0.5, ease: 'power2' });
      const rotationX = gsap.quickTo(card, 'rotationX', { duration: 0.5, ease: 'power2' });
      let frame = 0;
      let pointerX = 0;
      let pointerY = 0;

      card.addEventListener('pointermove', (event) => {
        pointerX = event.clientX;
        pointerY = event.clientY;
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const rect = card.getBoundingClientRect();
          rotationY(((pointerX - rect.left) / rect.width - 0.5) * 9);
          rotationX(-((pointerY - rect.top) / rect.height - 0.5) * 9);
        });
      }, { passive: true });

      card.addEventListener('pointerleave', () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        rotationY(0);
        rotationX(0);
      }, { passive: true });
    });
  }

  gsap.from('.btn-viewall', {
    scale: 0.5, autoAlpha: 0, ease: 'elastic.out(1, 0.5)', duration: 0.9,
    scrollTrigger: { trigger: '.btn-viewall', start: 'top 92%', once: true },
  });

  /* ---------- reviews ---------- */
  gsap.from('.reviews-title', {
    y: 60, autoAlpha: 0, ease: 'power3.out', duration: 0.8,
    scrollTrigger: { trigger: '.reviews', start: 'top 75%', once: true },
  });
  const reviews = gsap.utils.toArray('.review');
  if (reviews.length) {
    gsap.from(reviews, {
      x: (i) => (i % 2 ? 70 : -70),
      autoAlpha: 0,
      ease: 'power3.out',
      duration: 0.8,
      stagger: 0.12,
      scrollTrigger: { trigger: '.reviews-list', start: 'top 80%', once: true },
    });
  }
  gsap.from('.review-top img', {
    scale: 0,
    transformOrigin: 'left center',
    ease: 'elastic.out(1, 0.5)',
    duration: 0.8,
    stagger: 0.15,
    scrollTrigger: { trigger: '.reviews-list', start: 'top 80%', once: true },
  });

  /* ---------- footer ---------- */
  gsap.fromTo('.footer-bg',
    { yPercent: -6, scale: 1.12 },
    {
      yPercent: 0, scale: 1.12, ease: 'none',
      scrollTrigger: { trigger: '.site-footer', start: 'top bottom', end: 'bottom bottom', scrub: true },
    });
  gsap.from('.footer-cta h2, .footer-cta p', {
    y: 50, autoAlpha: 0, stagger: 0.1, ease: 'power3.out', duration: 0.8,
    scrollTrigger: { trigger: '.site-footer', start: 'top 70%', once: true },
  });
  gsap.from('.btn-locator', {
    scale: 0.4, autoAlpha: 0, ease: 'elastic.out(1, 0.5)', duration: 0.9, delay: 0.25,
    scrollTrigger: { trigger: '.site-footer', start: 'top 70%', once: true },
  });
  gsap.from('.fcol', {
    y: 40, autoAlpha: 0, stagger: 0.1, ease: 'power3.out', duration: 0.7,
    scrollTrigger: { trigger: '.footer-cols', start: 'top 92%', once: true },
  });
})();
